import { useMemo } from "react";
import { FilterConfig } from "@/components/cards/widgets/TableFilter";

interface UseUnifiedItemsFiltersProps {
  itemType: "blueprints" | "documents" | "library" | "dialogs" | "flows"| "variables";
  data: any[];
  tags: any[];
  statusWorkflows: Record<string, any>;
  blueprints?: Array<{ id: string; name: string }>;
}

/**
 * Hook to generate filter configurations for unified item pages
 * Consolidates the repetitive filter building logic for different item types
 */
export function useUnifiedItemsFilters({
  itemType,
  data,
  tags,
  statusWorkflows,
  blueprints = [],
}: UseUnifiedItemsFiltersProps): FilterConfig[] {
  return useMemo<FilterConfig[]>(() => {
    if (!data || !Array.isArray(data)) return [];

    // Templates/Blueprints filters: tags, status, creator, version
    if (itemType === "blueprints") {
      // Extract unique creators
      const creators = Array.from(new Set(data.map((item: any) => item.created_by || 'Unknown')))
        .sort()
        .map(creator => ({ value: creator, label: creator }));

      // Get statuses from status workflows for templates
      const statuses = Object.keys(statusWorkflows.templates || {})
        .sort()
        .map(status => ({ value: status, label: status }));

      // Extract unique versions
      const versions = Array.from(new Set(data.map((item: any) => item.version?.version).filter(Boolean)))
        .sort()
        .map(version => ({ value: String(version), label: `v${version}` }));

      return [
        {
          type: 'tags',
          label: 'Tags',
          options: tags.map(tag => ({ value: tag.name, label: tag.name }))
        },
        {
          type: 'status',
          label: 'Status',
          options: statuses
        },
        {
          type: 'creator',
          label: 'Creator',
          options: creators
        },
        {
          type: 'version',
          label: 'Version',
          options: versions
        },
      ];
    }

    // Library filters: status, tags, template, version
    if (itemType === "library") {
      // Get statuses from status workflows for objects (library)
      const statuses = Object.keys(statusWorkflows.objects || {})
        .sort()
        .map(status => ({ value: status, label: status }));

      // Extract unique templates/blueprints from data and look up their names
      const blueprintIds = Array.from(new Set(data.map((item: any) => item.blueprint_id).filter(Boolean)));
      const templates = blueprintIds
        .map(id => {
          const blueprint = blueprints.find(bp => bp.id === id);
          return { value: id, label: blueprint?.name || `Template ${id.substring(0, 8)}` };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      // Extract unique versions
      const versions = Array.from(new Set(data.map((item: any) => {
        if (item.version) {
          return typeof item.version === 'object' ? item.version.version : item.version;
        }
        return null;
      }).filter(Boolean)))
        .sort()
        .map(version => ({ value: String(version), label: `v${version}` }));

      return [
        {
          type: 'status',
          label: 'Status',
          options: statuses
        },
        {
          type: 'tags',
          label: 'Tags',
          options: tags.map(tag => ({ value: tag.name, label: tag.name }))
        },
        {
          type: 'template',
          label: 'Template',
          options: templates
        },
        {
          type: 'version',
          label: 'Version',
          options: versions
        },
      ];
    }

    // Documents filters: status, tags, creator
    if (itemType === "documents") {
      // Get statuses from status workflows for documents
      const statuses = Object.keys(statusWorkflows.documents || {})
        .sort()
        .map(status => ({ value: status, label: status }));

      // Extract unique creators
      const creators = Array.from(new Set(data.map((item: any) => item.created_by || 'Unknown')))
        .sort()
        .map(creator => ({ value: creator, label: creator }));

      return [
        {
          type: 'status',
          label: 'Status',
          options: statuses
        },
        {
          type: 'tags',
          label: 'Tags',
          options: tags.map(tag => ({ value: tag.name, label: tag.name }))
        },
        {
          type: 'creator',
          label: 'Creator',
          options: creators
        },
      ];
    }

    // Dialogs filters: status, tags, creator
    if (itemType === "dialogs") {
      // Get statuses from status workflows for dialogs
      const statuses = Object.keys(statusWorkflows.dialogs || {})
        .sort()
        .map(status => ({ value: status, label: status }));
      
      // Extract unique creators
      const creators = Array.from(new Set(data.map((item: any) => item.created_by || 'Unknown')))
        .sort()
        .map(creator => ({ value: creator, label: creator }));

      return [
        {
          type: 'status',
          label: 'Status',
          options: statuses
        },
        {
          type: 'tags',
          label: 'Tags',
          options: tags.map(tag => ({ value: tag.name, label: tag.name }))
        },
        {
          type: 'creator',
          label: 'Creator',
          options: creators
        },
      ];
    }
    // Variables filters: status, tags, creator
    if (itemType === "variables") {
      // Get statuses from status workflows for dialogs
      const statuses = Object.keys(statusWorkflows.variables || {})
        .sort()
        .map(status => ({ value: status, label: status }));
      
      // Extract unique creators
      const creators = Array.from(new Set(data.map((item: any) => item.created_by || 'Unknown')))
        .sort()
        .map(creator => ({ value: creator, label: creator }));

      return [
        {
          type: 'status',
          label: 'Status',
          options: statuses
        },
        {
          type: 'tags',
          label: 'Tags',
          options: tags.map(tag => ({ value: tag.name, label: tag.name }))
        },
        {
          type: 'creator',
          label: 'Creator',
          options: creators
        },
      ];
    }

    return [];
  }, [itemType, data, tags, statusWorkflows, blueprints]);
}

